/**
 * Binds one WHO HAS ACCESS row to the share hook's keyed handlers (SE-8313).
 * @module features/scouting/components/molecules/ShareAccessRow/ShareAccessRowContainer
 */

import { useCallback } from "react";

import ShareAccessRowView from "./ShareAccessRowView";

import { UserWatchlistRole } from "@/generated/graphql";
import {
  SHARE_ROLE_OPTIONS,
  isShareGrantRole,
  shareRoleLabel,
  shareRoleOptionLabel,
  type ShareAccessRowItem,
} from "@/features/scouting/utils/shareWatchlistGrants";

/** Props for `ShareAccessRowContainer`. */
export interface ShareAccessRowContainerProps {
  /** The grant to render. */
  readonly row: ShareAccessRowItem;
  /** Role change handler keyed by grant id. */
  readonly onChangeRole: (userWatchlistId: string, value: string) => void;
  /** Revoke handler keyed by grant id. */
  readonly onRevoke: (userWatchlistId: string) => void;
}

/**
 * Renders one access row with its handlers bound to the grant id.
 * @param root0 - Component props
 * @param root0.row - The grant to render
 * @param root0.onChangeRole - Role change handler keyed by grant id
 * @param root0.onRevoke - Revoke handler keyed by grant id
 * @returns The bound row view.
 */
const ShareAccessRowContainer = ({
  row,
  onChangeRole,
  onRevoke,
}: ShareAccessRowContainerProps) => {
  const handleRoleChange = useCallback(
    (value: string) => {
      onChangeRole(row.userWatchlistId, value);
    },
    [onChangeRole, row.userWatchlistId]
  );
  const handleRevoke = useCallback(() => {
    onRevoke(row.userWatchlistId);
  }, [onRevoke, row.userWatchlistId]);

  return (
    <ShareAccessRowView
      userId={row.userId}
      name={row.name}
      role={row.role}
      roleLabel={shareRoleLabel(row.role)}
      roleOptionLabel={
        isShareGrantRole(row.role) ? shareRoleOptionLabel(row.role) : ""
      }
      isOwner={row.role === UserWatchlistRole.Owner}
      isPending={row.isPending}
      caption={row.caption ?? null}
      isInactive={row.isInactive ?? false}
      roleOptions={SHARE_ROLE_OPTIONS}
      onRoleChange={handleRoleChange}
      onRevoke={handleRevoke}
    />
  );
};

ShareAccessRowContainer.displayName = "ShareAccessRowContainer";

export default ShareAccessRowContainer;
